"use client";
import Image from "@/components/Image/Image.component";
import { ShowContactActionKind } from "@/context/PageContext/PageContext.model";
import { usePageContext } from "@/context/PageContext/PageContextProvider";
import { cn } from "@/lib/utils";
import { ContentfulAsset } from "@/types/Contentful.model";
import { useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Typography } from "../Typography/Typography.component";
import OfficeDetails from "./OfficeDetails/OfficeDetails.component";
import { OfficesFields, OfficesPropsModel } from "./Offices.model";

const Offices = ({
  title,
  defaultBackgroundAsset,
  officesCollection,
}: OfficesPropsModel) => {
  const officeRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);

  const { dispatch } = usePageContext();
  const [backgroundImg, setBackgroundImg] = useState<ContentfulAsset>(
    defaultBackgroundAsset || officesCollection[0].backgroundAsset,
  );
  const [showOfficeDetails, setShowOfficeDeatils] = useState<boolean>(false);
  const [textColor, setTextColor] = useState<string>("text-core-white");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [selectedItem, setSelectedItem] = useState<OfficesFields>(
    officesCollection[0],
  );

  const changeBackgroundImage = (item: OfficesFields) => {
    setTextColor("text-core-white/20");
    setBackgroundImg(item.backgroundAsset);
  };

  const resetBackgroundImage = () => {
    setTextColor("text-core-white");
    setBackgroundImg(
      defaultBackgroundAsset || officesCollection[0].backgroundAsset,
    );
  };

  const onSelect = (item: OfficesFields, index: number) => {
    if (showOfficeDetails) {
      setShowOfficeDeatils(false);
      setTimeout(() => {
        setSelectedItem(item);
        setActiveIndex(index);
        setShowOfficeDeatils(true);
      }, 1000);
    } else {
      setSelectedItem(item);
      setActiveIndex(index);
      setShowOfficeDeatils(true);
    }
  };

  const revealContactModule = () => {
    dispatch({
      type: ShowContactActionKind.SET_CONTACT_STATUS,
      payload: { showContact: true },
    });
    setShowOfficeDeatils(false);
  };

  return (
    <div className="office overflow-x-hidden">
      <div className="max-m d:col-span-4 relative col-span-12 grid h-screen overflow-hidden max-lg:col-span-8">
        <div className="relative h-screen w-screen">
          <TransitionGroup>
            <CSSTransition
              nodeRef={backgroundRef}
              key={backgroundImg.url}
              timeout={500}
              classNames={{
                enter: "absolute opacity-0",
                enterActive:
                  "absolute opacity-100 transition-opacity duration-500 ease-in-out",
                enterDone: "absolute",
                exit: "absolute opacity-0",
              }}
            >
              <div
                ref={backgroundRef}
                className="absolute h-screen w-screen"
                style={{ transition: "all .5s ease-in-out" }}
              >
                <Image
                  src={`${backgroundImg.url}?fm=webp`}
                  alt={backgroundImg.title || selectedItem.city}
                  width={backgroundImg.width}
                  height={backgroundImg.height}
                  className="h-full w-full object-cover"
                  hasPlaceholder
                  priority
                />
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
        <div className="showcase__item__content px-xl py-xl max-md:px-sm gap-y-xl absolute flex h-screen w-screen flex-col justify-end">
          <div
            className={cn(
              textColor,
              "text-core-white grid cursor-pointer grid-cols-12 max-lg:grid-cols-8 max-md:grid-cols-4",
            )}
            onBlur={() => setTextColor("text-core-white")}
          >
            {officesCollection.map((item, index) => {
              return (
                <Typography.H5
                  key={index}
                  onMouseEnter={() => {
                    changeBackgroundImage(item);
                    setActiveIndex(index);
                  }}
                  onMouseLeave={() => {
                    resetBackgroundImage();
                    setActiveIndex(-1);
                  }}
                  onTouchStart={() => changeBackgroundImage(item)}
                  className={
                    "hover:text-core-white focus:text-core-white max-md:!text-h3 max-md:text-core-white col-span-12 flex w-max items-center outline-none"
                  }
                  onFocus={() => {
                    changeBackgroundImage(item);
                    setActiveIndex(index);
                  }}
                  onBlur={() => {
                    resetBackgroundImage();
                    setActiveIndex(-1);
                  }}
                  tabIndex={0}
                  onClick={() => onSelect(item, index)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      onSelect(item, index);
                    }
                  }}
                >
                  {item.city}

                  <Image
                    src={
                      index === activeIndex
                        ? "/static/icons/ForwardArrowFocused.png"
                        : "/static/icons/ForwardArrowSmall.png"
                    }
                    alt={`arrow-${index}`}
                    width={index === activeIndex ? 32 : 20}
                    height={index === activeIndex ? 32 : 16}
                    className={cn(
                      "mx-xs my-xxs transition-opacity duration-500 ease-in-out md:col-span-2",
                    )}
                  />
                </Typography.H5>
              );
            })}
          </div>
          <div className="col-span-12">
            <Typography.H1 className="text-core-white flex items-start">
              {title}
            </Typography.H1>
          </div>
          <CSSTransition
            in={showOfficeDetails}
            nodeRef={officeRef}
            timeout={1000}
            unmountOnExit
            classNames={{
              enter:
                "transition-translate duration-1000 animate-bounce-in-right",
              exit: "transition-translate duration-1000 animate-bounce-out-right",
              exitDone: "hidden",
            }}
          >
            <div
              className="bottom-xl right-xl absolute z-[100] max-md:right-0 max-md:bottom-0"
              ref={officeRef}
            >
              <OfficeDetails
                city={selectedItem.city}
                contactNumber={selectedItem.contactNumber}
                address={selectedItem.address}
                mapAsset={selectedItem.mapAsset}
                directionsLink={selectedItem.directionsLink}
                backgroundAsset={selectedItem.backgroundAsset}
                handleClickGetInTouch={revealContactModule}
                handleClickClose={setShowOfficeDeatils}
              />
            </div>
          </CSSTransition>
        </div>
      </div>
    </div>
  );
};
export default Offices;
